import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Activity } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { isAdminOrStaff } from '@/utils/roleCheck';

interface AudioDebugBadgeProps {
  moduleId: string;
  courseId: string;
  audioUrl: string | null;
  isLoading: boolean;
  error: string | null;
  loadTime?: number;
  cacheHit?: boolean;
}

/**
 * Audio Debug Badge - Admin/Staff only
 * Shows real-time audio status for debugging
 * Only visible to users with admin or staff role
 */
export const AudioDebugBadge: React.FC<AudioDebugBadgeProps> = ({
  moduleId,
  courseId,
  audioUrl,
  isLoading,
  error,
  loadTime,
  cacheHit
}) => {
  const [isVisible, setIsVisible] = useState(false);

  // Only show to admin/staff users (server-side role check)
  useEffect(() => {
    const checkVisibility = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsVisible(false);
        return;
      }
      
      // Check if user has admin or staff role (server-side validation)
      const hasAdminAccess = await isAdminOrStaff(user.id);
      setIsVisible(hasAdminAccess);
    };

    checkVisibility();
  }, []);

  if (!isVisible) return null;

  const getStatus = () => {
    if (error) return { label: 'Error', variant: 'destructive' as const };
    if (isLoading) return { label: 'Loading...', variant: 'secondary' as const };
    if (!audioUrl) return { label: 'No Audio', variant: 'outline' as const };
    return { label: 'Ready', variant: 'default' as const };
  };

  const status = getStatus();

  return (
    <div className="fixed bottom-4 left-4 z-50 space-y-2">
      <Badge variant={status.variant} className="flex items-center gap-2">
        <Activity className="h-3 w-3" />
        Audio: {status.label}
      </Badge>
      
      {loadTime !== undefined && (
        <Badge variant="outline" className="block">
          {loadTime}ms {cacheHit ? '(cached)' : '(fresh)'}
        </Badge>
      )}
      
      {error && (
        <Badge variant="destructive" className="block text-xs">
          {error}
        </Badge>
      )}
      
      <div className="text-xs text-muted-foreground bg-background/80 backdrop-blur-sm p-2 rounded border">
        <div>Module: {moduleId.substring(0, 12)}...</div>
        <div>Course: {courseId.substring(0, 12)}...</div>
      </div>
    </div>
  );
};
